import { defineStore } from "pinia";

import { useCollectionsStore } from "./collectionsStore";
import { useTagStore } from "./tagStore";

export const useProductsStore = defineStore("productsStore", {
  state: () => ({
    products: [],
    product: null,
    productId: null,
    totalProducts: 0,
    searchProducts: null,
    loadingProducts: false,
    productsCountSelectedCollection: 0,
    productsCountTotal: 0,
    nextProducts: null,
    getProductsTrigger: 0,
    imagePreviewTrigger: 0,
    forceTableProductsUpdate: 0,
    nextProductsUrl: "",
    triggerCreateProduct: null,
    searchProductsForce: false,
    selectedProducts: [],
    allProductsSelected: false,
    isBulkSelectionMode: false,
    selectedProductIds: [],
    isBulkSelectAllFiltered: false,
    sourceProductForBranding: null,
  }),
  getters: {
    hasSelectedProducts: (state) => {
      return (
        state.allProductsSelected ||
        state.selectedProducts?.length > 0 ||
        state.isBulkSelectAllFiltered ||
        state.selectedProductIds?.length > 0
      );
    },
    isProductBulkSelected: (state) => (productId) => {
      const id =
        typeof productId === "object" && productId !== null ? (productId.id ?? productId.uuid) : productId;
      return state.isBulkSelectAllFiltered || state.selectedProductIds.includes(id);
    },
    hasBulkSelectedProducts: (state) => {
      return state.isBulkSelectAllFiltered || state.selectedProductIds?.length > 0;
    },
    bulkSelectedCount: (state) => {
      return state.isBulkSelectAllFiltered
        ? state.productsCountSelectedCollection
        : state.selectedProductIds.length;
    },
  },
  actions: {
    set_product_models(models) {
      if (this.product) {
        this.product.models = models;
      }
    },
    setValue(keyValue) {
      if (keyValue?.key in this) {
        this[keyValue.key] = keyValue.value;
      }
    },
    deleteProductByID(productId) {
      this.products = this.products.filter((_product) => _product.id !== productId);
      this.productsCountSelectedCollection = this.products.length;
      this.productsCountTotal--;
    },
    addProduct(product) {
      this.products.unshift(product);
      this.productsCountSelectedCollection++;
      this.productsCountTotal++;
    },
    updateProduct(product) {
      if (Array.isArray(this.products)) {
        const productIndex = this.products.findIndex((_product) => _product.id === product.id);
        if (productIndex !== -1) {
          this.products.splice(productIndex, 1, { ...this.products[productIndex], ...product });
          this.forceTableProductsUpdate++;
        }
      } else if (typeof this.products === "object" && this.products !== null) {
        this.products = { ...this.products, ...product };
        this.forceTableProductsUpdate++;
      }
    },
    updateProductsAll(products) {
      this.products = products;
    },
    removeFolderFromProducts(folderId) {
      this.products.forEach((product) => {
        if (product.shaders && Array.isArray(product.shaders)) {
          product.shaders.forEach((shader) => {
            if (shader.libraries && Array.isArray(shader.libraries)) {
              shader.libraries = shader.libraries.filter((lib) => lib.id !== folderId);
            }
          });
        }
      });
    },
    updateMaterialInProducts(material) {
      this.products.forEach((product) => {
        const libraries = Array.isArray(product.libraries)
          ? product.libraries
          : Array.isArray(product.shaders)
            ? product.shaders.reduce((acc, shader) => {
                if (Array.isArray(shader.libraries)) {
                  acc.push(...shader.libraries);
                }
                return acc;
              }, [])
            : [];

        const targetLibrary = libraries.find((lib) => lib.id === material.library_id);

        if (targetLibrary && Array.isArray(targetLibrary.materials)) {
          const materialIndex = targetLibrary.materials.findIndex((m) => m.id === material.id);

          if (materialIndex !== -1) {
            targetLibrary.materials[materialIndex] = {
              ...targetLibrary.materials[materialIndex],
              ...material,
            };
          }
        }
      });
    },
    addTagToProduct({ productId, tag }) {
      const product = this.products.find((p) => p.id === productId);

      if (product) {
        const updatedProduct = { ...product };

        if (!updatedProduct.tags) {
          updatedProduct.tags = [];
        }

        const tagExists = updatedProduct.tags.some((existingTag) => existingTag.id === tag.id);

        if (!tagExists) {
          updatedProduct.tags = [...updatedProduct.tags, tag];

          const index = this.products.findIndex((p) => p.id === productId);
          if (index !== -1) {
            this.products.splice(index, 1, updatedProduct);
            this.forceTableProductsUpdate++;

            if (this.product && this.product.id === productId) {
              this.product = { ...updatedProduct };
            }
          }
        }
      }
    },
    removeTagFromProduct({ productId, tagId }) {
      const productIndex = this.products.findIndex((p) => p.id === productId);
      if (productIndex !== -1 && this.products[productIndex].tags) {
        const initialLength = this.products[productIndex].tags.length;
        this.products[productIndex].tags = this.products[productIndex].tags.filter((tag) => tag.id !== tagId);

        if (initialLength !== this.products[productIndex].tags.length) {
          this.forceTableProductsUpdate++;
        }
      }
    },

    addSelectedProduct(product) {
      const getId = (p) => p?.uuid;
      const id = getId(product);
      const exists = this.selectedProducts.some((p) => getId(p) === id);
      if (!exists) this.selectedProducts.push(product);
      this.allProductsSelected = false;
    },
    removeSelectedProduct(product) {
      const getId = (p) => p?.id ?? p?.uuid ?? p;
      const id = getId(product);
      this.selectedProducts = this.selectedProducts.filter((p) => getId(p) !== id);
      this.allProductsSelected = false;
    },

    setSelectedProducts(products) {
      this.selectedProducts = products;
      this.allProductsSelected = false;
    },
    setAllProductsSelected(isSelected) {
      this.allProductsSelected = isSelected;
      if (isSelected) {
        this.selectedProducts = [];
      }
    },
    clearAllProducts() {
      this.selectedProducts = [];
      this.allProductsSelected = false;
    },
    selectAllProducts() {
      this.allProductsSelected = true;
      this.selectedProducts = [];
    },
    setProductTags({ productId, tags }) {
      const productIndex = this.products.findIndex((p) => p.id === productId);
      if (productIndex !== -1) {
        this.products[productIndex].tags = tags || [];
        this.forceTableProductsUpdate++;
      }
    },
    updateTagInAllProducts(updatedTag) {
      this.products.forEach((product) => {
        if (product.tags && Array.isArray(product.tags)) {
          const tagIndex = product.tags.findIndex((tag) => tag.id === updatedTag.id);
          if (tagIndex !== -1) {
            product.tags.splice(tagIndex, 1, { ...product.tags[tagIndex], ...updatedTag });
          }
        }
      });

      this.forceTableProductsUpdate++;
    },
    removeTagFromAllProducts(tagId) {
      this.products.forEach((product) => {
        if (product.tags && Array.isArray(product.tags)) {
          product.tags = product.tags.filter((tag) => tag.id !== tagId);
        }
      });
      this.forceTableProductsUpdate++;
    },

    SET_VALUE(keyValue) {
      if (keyValue.key === "product.models") {
        this.set_product_models(keyValue.value);
      } else this.setValue(keyValue);
    },
    DELETE_PRODUCT_BY_ID(productId) {
      const productToDelete = this.products.find((product) => product.id === productId);
      if (productToDelete) {
        const collectionsStore = useCollectionsStore();
        collectionsStore.decrementCollection(productToDelete.collection_id);

        if (productToDelete.tags && Array.isArray(productToDelete.tags)) {
          const tagStore = useTagStore();
          productToDelete.tags.forEach((tag) => {
            tagStore.DECREMENT_TAG_COUNT(tag.id);
          });
        }
      }
      this.deleteProductByID(productId);
    },
    ADD_PRODUCT(product) {
      const collectionsStore = useCollectionsStore();
      const selectedCollection = collectionsStore.selectedCollection;
      const inSelectedCollection = selectedCollection.some(
        (collection) => collection.id === product.collection_id
      );
      if (inSelectedCollection) {
        this.addProduct(product);
      }
      collectionsStore.incrementCollection(product.collection_id);

      if (product.tags && Array.isArray(product.tags)) {
        const tagStore = useTagStore();
        product.tags.forEach((tag) => {
          tagStore.INCREMENT_TAG_COUNT(tag.id);
        });
      }
    },
    PUT_PRODUCT(product) {
      if (!product || !product.id) {
        console.warn("cancel put product. Product has no ID", product);
        return;
      }

      const productExist = Array.isArray(this.products)
        ? this.products.some((_product) => _product.id === product.id)
        : this.products?.id === product.id;

      if (productExist) {
        const productBeforeUpdate = this.products.find((_prod) => _prod.id === product.id);
        const changeProductCollection = productBeforeUpdate.collection_id !== product.collection_id;
        if (changeProductCollection) {
          this.DELETE_PRODUCT_BY_ID(productBeforeUpdate.id);
          this.ADD_PRODUCT(product);
        } else {
          this.updateProduct(product);
        }
      } else {
        this.ADD_PRODUCT(product);
      }
    },
    UPDATE_PRODUCTS_ALL(products) {
      this.updateProductsAll(products);
    },
    UPDATE_MATERIAL_IN_PRODUCTS(material) {
      this.updateMaterialInProducts(material);
    },
    async ADD_TAG_TO_PRODUCT({ productId, tag }) {
      try {
        this.addTagToProduct({ productId, tag });
        return true;
      } catch (error) {
        console.error("Ошибка при добавлении тега:", error);
        return false;
      }
    },
    async REMOVE_TAG_FROM_PRODUCT({ productId, tagId }) {
      try {
        this.removeTagFromProduct({ productId, tagId });
        return true;
      } catch (error) {
        console.error("Ошибка при удалении тега:", error);
        return false;
      }
    },
    async UPDATE_PRODUCT_TAGS({ productId, tags }) {
      try {
        this.setProductTags({ productId, tags });
        return true;
      } catch (error) {
        console.error("Ошибка при обновлении тегов:", error);
        return false;
      }
    },
    async TOGGLE_PRODUCT_TAG({ productId, tag }) {
      const product = this.products.find((p) => p.id === productId);

      if (!product) {
        console.warn("Продукт не найден:", productId);
        return false;
      }

      const hasTag = product.tags && product.tags.some((t) => t.id === tag.id);

      if (hasTag) {
        return await this.REMOVE_TAG_FROM_PRODUCT({
          productId,
          tagId: tag.id,
        });
      } else {
        return await this.ADD_TAG_TO_PRODUCT({
          productId,
          tag,
        });
      }
    },
    async UPDATE_TAG_IN_ALL_PRODUCTS(updatedTag) {
      try {
        this.updateTagInAllProducts(updatedTag);
        return true;
      } catch (error) {
        console.error("Ошибка при обновлении тега во всех продуктах:", error);
        return false;
      }
    },

    ADD_SELECTED_PRODUCT(product) {
      this.addSelectedProduct(product);
    },
    REMOVE_SELECTED_PRODUCT(product) {
      this.removeSelectedProduct(product);
    },
    CLEAR_ALL_PRODUCTS() {
      this.clearAllProducts();
    },
    SELECT_ALL_PRODUCTS() {
      this.selectAllProducts();
    },
    SET_ALL_PRODUCTS_SELECTED(isSelected) {
      this.setAllProductsSelected(isSelected);
    },
    SYNC_PRODUCT_TAGS({ productId, serverTags }) {
      const productIndex = this.products.findIndex((p) => p.id === productId);
      if (productIndex !== -1) {
        this.setProductTags({ productId, tags: serverTags });
      }
    },
    REMOVE_TAG_FROM_ALL_PRODUCTS(tagId) {
      this.removeTagFromAllProducts(tagId);
      return true;
    },

    startBulkSelection(sourceProduct = null) {
      this.isBulkSelectionMode = true;
      this.sourceProductForBranding = sourceProduct;
      this.selectedProductIds = [];
      this.isBulkSelectAllFiltered = false;
    },

    cancelBulkSelection() {
      this.isBulkSelectionMode = false;
      this.sourceProductForBranding = null;
      this.selectedProductIds = [];
      this.isBulkSelectAllFiltered = false;
    },

    toggleProductSelection(productId) {
      const id =
        typeof productId === "object" && productId !== null ? (productId.id ?? productId.uuid) : productId;
      const index = this.selectedProductIds.indexOf(id);
      if (index === -1) {
        this.selectedProductIds.push(id);
      } else {
        this.selectedProductIds.splice(index, 1);
      }
      this.isBulkSelectAllFiltered = false;
    },

    addSelectedProductId(productId) {
      const id =
        typeof productId === "object" && productId !== null ? (productId.id ?? productId.uuid) : productId;
      if (!this.selectedProductIds.includes(id)) {
        this.selectedProductIds.push(id);
      }
      this.isBulkSelectAllFiltered = false;
    },

    removeSelectedProductId(productId) {
      const id =
        typeof productId === "object" && productId !== null ? (productId.id ?? productId.uuid) : productId;
      this.selectedProductIds = this.selectedProductIds.filter((item) => item !== id);
      this.isBulkSelectAllFiltered = false;
    },

    setSelectedProductIds(ids) {
      this.selectedProductIds = Array.isArray(ids) ? [...ids] : [];
      this.isBulkSelectAllFiltered = false;
    },

    setBulkSelectAllFiltered(isSelected) {
      this.isBulkSelectAllFiltered = Boolean(isSelected);
      if (this.isBulkSelectAllFiltered) {
        this.selectedProductIds = [];
      }
    },

    setSourceProductForBranding(product) {
      this.sourceProductForBranding = product;
    },

    START_BULK_SELECTION(sourceProduct) {
      this.startBulkSelection(sourceProduct);
    },
    CANCEL_BULK_SELECTION() {
      this.cancelBulkSelection();
    },
    TOGGLE_PRODUCT_SELECTION(productId) {
      this.toggleProductSelection(productId);
    },
    SET_SELECTED_PRODUCT_IDS(ids) {
      this.setSelectedProductIds(ids);
    },
    SET_BULK_SELECT_ALL_FILTERED(isSelected) {
      this.setBulkSelectAllFiltered(isSelected);
    },
    SET_SOURCE_PRODUCT_FOR_BRANDING(product) {
      this.setSourceProductForBranding(product);
    },
  },
});

export default useProductsStore;
